import { singleTags, openInlineTags, openBlockTags, closeTags } from './config'
import { attributesToString, firstResult, htmlEscape } from '../utils'
import * as st from '../../components/Link/Link.module.scss'
const tagRegEx = /\{(\w+)\|?([^\}]+?)?\}/g
const END_TAG = 'end'

// Condition helpers
const isSingleTag:any = (tag:any) => singleTags.hasOwnProperty(tag)
const isInlineTag:any = (tag:any) => openInlineTags.hasOwnProperty(tag)
const isBlockTag:any = (tag:any) => openBlockTags.hasOwnProperty(tag)
const isEndTag: any = (tag: any) => tag === END_TAG

// Map tags
const getTagForMap = (map:any) => (key:any) => {
  const tag = map[key]
  return typeof tag === 'undefined' ? '' : tag
}
const getInlineTag:any = getTagForMap(openInlineTags)
const getBlockTag:any = getTagForMap(openBlockTags)
const replaceEndTag:any = getTagForMap(closeTags)
const replaceSingleTag:any = getTagForMap(singleTags)

// Construct HTML Tags
const openTagMarkup = (tag:any, attrs:any) =>
  ['<', tag, attributesToString(attrs), '>'].join('')
const replaceInlineTag = (tag:any, attrs:any) => openTagMarkup(getInlineTag(tag), attrs)
const replaceBlockTag = (tag:any, attrs:any) => openTagMarkup(getBlockTag(tag), attrs)
const replaceOpenTag = (tag:any, attrs:any) =>
  isInlineTag(tag)
    ? replaceInlineTag(tag, attrs)
    : isBlockTag(tag)
      ? replaceBlockTag(tag, attrs)
      : ''

// Stack helpers
const closeTagsReducer = (acc:any, tag:any) => acc + replaceEndTag(tag)
const openTagsReducer = (acc:any, tag:any) => acc + replaceOpenTag(tag, undefined)
const closeStack = (stack:any) => {
  if (stack.length < 1) {
    return ''
  }
  const closingTags = stack.reverse().reduce(closeTagsReducer, '')
  stack.length = 0 // empty the stack once we have all the closing tags
  return closingTags
}
const closeStacks = (inlineStack:any, blockStack:any) =>
  closeStack(inlineStack) + closeStack(blockStack)

// Prevent Nesting tags
const preventNesting = (tag:any, stack:any) => {
  if (!stack.includes(tag)) {
    return ''
  }

  const index = stack.indexOf(tag)
  const openedTagsSinceDupe = stack.slice(index + 1)

  // remove the dupe from the stack
  stack.splice(index, 1)

  return [
    // close all open tags since the dupe
    openedTagsSinceDupe.reduceRight(closeTagsReducer, ''),

    // close the dupe
    replaceEndTag(tag),

    // reopen the closed tags since the dupe
    openedTagsSinceDupe.reduce(openTagsReducer, ''),
  ].join('')
}

const preventBlockNesting:any = (tag:any, inlineStack:any, blockStack:any):any => {
  let closedTags = closeStack(inlineStack)
  closedTags += preventNesting(tag, blockStack)
  return closedTags
}

// Handle Tags
const handleInlineTag:any = (tag:any, attrs:any, inlineStack:any):any => {
  const closedTags = preventNesting(tag, inlineStack)
  inlineStack.push(tag)
  return closedTags + replaceInlineTag(tag, attrs)
}

const handleBlockTag:any = (tag:any, attrs:any, inlineStack:any, blockStack:any):any => {
  const closedTags = preventBlockNesting(tag, inlineStack, blockStack)
  blockStack.push(tag)
  return closedTags + replaceBlockTag(tag, attrs)
}

// Tag replace closure
const tagReplacer = (
  inlineStack: string[],
  blockStack: string[],
  args?: any
) => (_:any, ...rest:any[]) => {
  const [tag, attrName] = rest
  const attrMap = (args && args.attrs) || {}
  const isLegacyLinkSyntax =
    tag === 'link' && attrName && typeof attrMap[attrName] === 'undefined'
  const attrs = isLegacyLinkSyntax ? { href: attrName, class: st.link } : attrMap[attrName]

  const inlineHandler = () =>
    isInlineTag(tag) && handleInlineTag(tag, attrs, inlineStack)
  const blockHandler = () =>
    isBlockTag(tag) && handleBlockTag(tag, attrs, inlineStack, blockStack)
  const endHandler = () =>
    isEndTag(tag) && replaceEndTag(inlineStack.pop() || blockStack.pop())
  const singleTagHandler = () => isSingleTag(tag) && replaceSingleTag(tag)
  const fallback = () => ''

  return firstResult(
    inlineHandler,
    blockHandler,
    endHandler,
    singleTagHandler,
    fallback
  )
}

/**
 * JavaScript Implementation of the Perl replaceTags used by the Localizer
 *
 * @param {DVString | String} string - Either a simple string or a DVString
 * @param {Object} args - parameters
 * @param {Object} args.attrs - attribute map to use on tags
 *
 * @example
 * // returns 'simple example'
 * replaceTags('simple example')
 *
 *
 * @example
 * // returns 'text with <a href="https://test.com?foo=%3C%3E" title="my link">link</a>'
 * replaceTags(
 *  'text with {link|myAttrs}link{end}',
 *  {
 *    attrs: {
 *      myAttrs: {
 *        href: 'https://test.com?foo=<>',
 *        title: 'my link'
 *      }
 *    }
 *  }
 *
 *
 * @returns {String} Potentially Containing HTML
 *
 */
export const replaceTags = (string: any, args?: any) => {
  const inlineStack:any[] = []
  const blockStack:any[] = []

  if (typeof string === 'undefined' || string === null) {
    return ''
  }

  // Run string through a replace closure returned by the tagRaplecer.
  // TODO: Once WPS stop sending markup htmlEscape() the string first
  return (
    htmlEscape(`${string}`).replace(tagRegEx, tagReplacer(inlineStack, blockStack, args)) +
    closeStacks(inlineStack, blockStack)
  ) // close all open tags to return valid markup
}

const isStringObj = (DVString: any) => (
  typeof DVString === 'object' &&
    DVString !== null &&
    typeof DVString.string !== 'undefined' &&
    typeof DVString.attrs !== 'undefined'
)

const stringFromDVString = (DVString: any) => {
  if (!DVString.string) {
    return ''
  }
  const attrs = DVString.attrs
  return replaceTags(DVString.string, {attrs})
}

export const convertString = (string: any)  => (
  isStringObj(string) ? stringFromDVString(string) : replaceTags(string)
)

export const tagReplacers = [
  ...Object.keys(singleTags),
  ...Object.keys(openInlineTags),
  ...Object.keys(openBlockTags),
  END_TAG
]
